import React from 'react';
import { api } from '../../lib/api.js';
import { PlatformOrgsGroup } from './PlatformOrgsGroup.jsx';
import { normalizeUrl, isHubspotUrl } from '../../lib/url.js';
import { EditableGroup } from '../../components/EditableGroup.jsx';
import { EditableList } from '../../components/EditableList.jsx';
import { UserMultiSelect } from '../../components/UserMultiSelect.jsx';
import {
  ClientStatusBadge,
  ProjectLabel,
  useChoice,
} from '../../components/ui.jsx';
import { Select } from '../../components/controls/Select.jsx';
import { DatePicker } from '../../components/controls/DatePicker.jsx';
import { StatusBadge } from '../Projects.jsx';
import {
  CLIENT_STATUS_LABEL,
  clientBody,
  clientStatus,
  projectList,
  saveConfirmingActiveProjects,
} from '../../lib/clientStatus.js';

const HEALTH_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'watch', label: 'Watch' },
  { value: 'at_risk', label: 'At risk' },
];

export const HEALTH_LABEL = {
  healthy: 'Healthy',
  watch: 'Watch',
  at_risk: 'At risk',
};
const HEALTH_TONE = {
  healthy: 'status-active',
  watch: 'status-paused',
  at_risk: 'status-completed',
};

export function HealthBadge({ health }) {
  if (!health) return <span className="muted-text">Not set</span>;
  return (
    <span className={`project-status-badge ${HEALTH_TONE[health] || ''}`}>
      {HEALTH_LABEL[health]}
    </span>
  );
}

const CLIENT_STATUS_OPTIONS = Object.entries(CLIENT_STATUS_LABEL).map(
  ([value, label]) => ({ value, label }),
);

const orDash = (value) =>
  value ? value : <span className="muted-text">—</span>;

const namesFor = (users, ids) =>
  (ids || [])
    .map((id) => users.find((user) => user.id === id)?.name)
    .filter(Boolean);

// The bootstrap user list is scoped; merge in the assignees the detail
// payload reports so the picker can show / remove an out-of-scope one.
const mergeUsers = (base, extra) => {
  const seen = new Set(base.map((user) => user.id));
  return [...base, ...(extra || []).filter((user) => !seen.has(user.id))];
};

const CONTACT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'organisation', label: 'Organisation' },
  {
    key: 'preferredChannel',
    label: 'Preferred channel',
    type: 'select',
    options: ['', 'Teams', 'Email', 'Phone', 'Other'],
  },
  {
    key: 'responsibility',
    label: 'Responsibility',
    type: 'textarea',
    full: true,
  },
];

export function ClientInfoTab({
  client,
  data,
  detail,
  reload,
  reloadDetail,
  onOpenProject,
  editingGroup,
  setEditingGroup,
}) {
  const canManageAll = data.canManageAll;
  const relationship = detail.relationship;
  const users = mergeUsers(data.users, detail.assignedUsers);
  const [ask, choiceModal] = useChoice();

  // Each group's Save rebuilds a whole client / profile / relationship body
  // from the current snapshot, so two groups saving before the first reload
  // lands would revert each other. Only one group edits at a time. This lock
  // is owned by ClientDetail (not local state here): this tab unmounts on
  // every tab switch, and a component-local lock would forget an in-flight
  // save the moment the user switches away and back, letting a second group
  // submit its own stale snapshot and silently revert the first.
  const groupLock = (key) => ({
    canEdit: editingGroup === null || editingGroup === key,
    onEditingChange: (on) => setEditingGroup(on ? key : null),
  });

  async function putClient(overrides) {
    await api(`/clients/${client.id}`, {
      method: 'PUT',
      body: JSON.stringify(clientBody(client, overrides)),
    });
    await reload();
  }

  // The API refuses to deactivate a client that still has active projects
  // (409 + the list): ask whether to close those projects too, or change
  // nothing. Resolves false when the user backs out.
  function saveClientBasics(fields) {
    return saveConfirmingActiveProjects(
      (overrides) => putClient({ ...fields, ...overrides }),
      async (open) =>
        (await ask({
          title: 'Client still has active projects',
          message: `${client.name} still has ${open.length} active project${open.length === 1 ? '' : 's'}: ${projectList(open)}. Deactivate the client and mark ${open.length === 1 ? 'that project' : 'those projects'} Completed, or leave everything as it is?`,
          choices: [
            { value: 'keep', label: 'Change nothing' },
            {
              value: 'both',
              label: 'Deactivate client and projects',
              tone: 'danger',
            },
          ],
        })) === 'both',
    );
  }

  async function putClientProfile(overrides) {
    const hubspotUrl = normalizeUrl(
      'hubspotUrl' in overrides
        ? overrides.hubspotUrl
        : client.hubspotUrl || '',
    );
    if (hubspotUrl && !isHubspotUrl(hubspotUrl)) {
      throw new Error('HubSpot must be a hubspot.com link (or left empty).');
    }
    await api(`/clients/${client.id}/profile`, {
      method: 'PUT',
      body: JSON.stringify({
        tier: client.tier || 'Standard',
        groupName: client.groupName || '',
        clientSince: client.clientSince || null,
        accountManagerIds: client.accountManagerIds || [],
        ...overrides,
        hubspotUrl,
      }),
    });
    await reload();
  }

  // Send only the slice being edited; the endpoint merges it, so the Overview
  // card and the reasons list can't overwrite each other's untouched fields.
  async function putRelationship(patch) {
    await api(`/clients/${client.id}/relationship`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    await reloadDetail();
  }

  const saveContacts = async (items) => {
    await api(`/clients/${client.id}/contacts`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    await reloadDetail();
  };

  return (
    <div className="project-info-tab">
      {choiceModal}
      <section className="card">
        <div className="card-head">
          <h2>Client profile</h2>
        </div>
        <div className="info-groups">
          <EditableGroup
            title="Basics"
            {...groupLock('basics')}
            initialForm={() => ({
              name: client.name,
              tier: client.tier || 'Standard',
              groupName: client.groupName || '',
              clientSince: client.clientSince || '',
              hubspotUrl: client.hubspotUrl || '',
              status: clientStatus(client),
            })}
            onSave={async (f) => {
              if (
                canManageAll &&
                !(await saveClientBasics({
                  name: f.name,
                  tier: f.tier,
                  groupName: f.groupName,
                  active: f.status === 'active',
                }))
              )
                return false;
              await putClientProfile({
                tier: f.tier,
                groupName: f.groupName,
                clientSince: f.clientSince || null,
                hubspotUrl: f.hubspotUrl,
              });
            }}
            fields={[
              {
                key: 'name',
                label: 'Name',
                read: client.name,
                edit: canManageAll
                  ? (f, set) => (
                      <input
                        value={f.name}
                        onChange={(e) => set({ name: e.target.value })}
                      />
                    )
                  : undefined,
              },
              {
                key: 'status',
                label: 'Status',
                read: <ClientStatusBadge client={client} />,
                edit: canManageAll
                  ? (f, set) => (
                      <Select
                        ariaLabel="Client status"
                        value={f.status}
                        onChange={(status) => set({ status })}
                        options={CLIENT_STATUS_OPTIONS}
                      />
                    )
                  : undefined,
              },
              {
                key: 'tier',
                label: 'Tier',
                read: orDash(client.tier),
                edit: (f, set) => (
                  <input
                    value={f.tier}
                    onChange={(e) => set({ tier: e.target.value })}
                  />
                ),
              },
              {
                key: 'group',
                label: 'Group',
                read: orDash(client.groupName),
                edit: (f, set) => (
                  <input
                    value={f.groupName}
                    maxLength={60}
                    onChange={(e) => set({ groupName: e.target.value })}
                  />
                ),
              },
              {
                key: 'clientSince',
                label: 'Client since',
                read: orDash(client.clientSince),
                edit: (f, set) => (
                  <DatePicker
                    ariaLabel="Client since"
                    value={f.clientSince}
                    onChange={(clientSince) => set({ clientSince })}
                  />
                ),
              },
              {
                key: 'projects',
                label: 'Projects',
                read: String(client.projects.length),
              },
              {
                key: 'hubspot',
                label: 'HubSpot',
                read: client.hubspotUrl ? (
                  <a
                    href={client.hubspotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in HubSpot ↗
                  </a>
                ) : null,
                edit: (f, set) => (
                  <input
                    type="text"
                    inputMode="url"
                    value={f.hubspotUrl}
                    onChange={(e) => set({ hubspotUrl: e.target.value })}
                  />
                ),
              },
            ]}
          />

          <EditableGroup
            title="Account team"
            {...groupLock('accountTeam')}
            initialForm={() => ({
              accountManagerIds: client.accountManagerIds || [],
            })}
            onSave={(f) =>
              putClientProfile({ accountManagerIds: f.accountManagerIds })
            }
            fields={[
              {
                key: 'accountManagers',
                label: 'Account managers',
                read:
                  namesFor(users, client.accountManagerIds).join(', ') || null,
                edit: (f, set) => (
                  <UserMultiSelect
                    users={users}
                    selected={f.accountManagerIds}
                    onChange={(accountManagerIds) => set({ accountManagerIds })}
                  />
                ),
              },
              {
                key: 'health',
                label: 'Relationship health',
                read: <HealthBadge health={relationship.health} />,
              },
              {
                key: 'cadence',
                label: 'Contact cadence',
                read: orDash(relationship.contactCadence),
              },
            ]}
          />

          <PlatformOrgsGroup
            clientId={client.id}
            orgs={detail.platformOrgs}
            isAdmin={canManageAll}
            {...groupLock('platformOrgs')}
            reloadDetail={reloadDetail}
          />
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Relationship</h2>
          <p className="section-subtitle">Explainable — no numeric score</p>
        </div>
        <div className="info-groups">
          <EditableGroup
            title="Overview"
            {...groupLock('overview')}
            initialForm={() => ({
              health: relationship.health || '',
              contactCadence: relationship.contactCadence || '',
              lastMeaningfulInteraction:
                relationship.lastMeaningfulInteraction || '',
              lastStrategicReview: relationship.lastStrategicReview || '',
              nextCheckpoint: relationship.nextCheckpoint || '',
            })}
            onSave={(f) =>
              putRelationship({
                health: f.health || null,
                contactCadence: f.contactCadence.trim(),
                lastMeaningfulInteraction: f.lastMeaningfulInteraction || null,
                lastStrategicReview: f.lastStrategicReview || null,
                nextCheckpoint: f.nextCheckpoint.trim(),
              })
            }
            fields={[
              {
                key: 'health',
                label: 'Health',
                read: <HealthBadge health={relationship.health} />,
                edit: (f, set) => (
                  <Select
                    ariaLabel="Health"
                    value={f.health}
                    onChange={(health) => set({ health })}
                    options={HEALTH_OPTIONS}
                  />
                ),
              },
              {
                key: 'lastMeaningfulInteraction',
                label: 'Last meaningful interaction',
                read: orDash(relationship.lastMeaningfulInteraction),
                edit: (f, set) => (
                  <DatePicker
                    ariaLabel="Last meaningful interaction"
                    value={f.lastMeaningfulInteraction}
                    onChange={(lastMeaningfulInteraction) =>
                      set({ lastMeaningfulInteraction })
                    }
                  />
                ),
              },
              {
                key: 'lastStrategicReview',
                label: 'Last strategic review',
                read: orDash(relationship.lastStrategicReview),
                edit: (f, set) => (
                  <DatePicker
                    ariaLabel="Last strategic review"
                    value={f.lastStrategicReview}
                    onChange={(lastStrategicReview) =>
                      set({ lastStrategicReview })
                    }
                  />
                ),
              },
              {
                key: 'nextCheckpoint',
                label: 'Recommended next checkpoint',
                read: orDash(relationship.nextCheckpoint),
                edit: (f, set) => (
                  <input
                    value={f.nextCheckpoint}
                    onChange={(e) => set({ nextCheckpoint: e.target.value })}
                  />
                ),
              },
              {
                key: 'contactCadence',
                label: 'Contact cadence',
                read: orDash(relationship.contactCadence),
                edit: (f, set) => (
                  <input
                    value={f.contactCadence}
                    onChange={(e) => set({ contactCadence: e.target.value })}
                  />
                ),
              },
            ]}
          />

          <ReasonsEditor
            notes={relationship.healthNotes}
            onSave={(healthNotes) => putRelationship({ healthNotes })}
          />
        </div>
      </section>

      <EditableList
        title="Key contacts"
        items={detail.contacts}
        fields={CONTACT_FIELDS}
        addLabel="Add contact"
        emptyLabel="No key contacts recorded yet."
        toForm={(c) => ({
          name: c.name || '',
          role: c.role || '',
          organisation: c.organisation || '',
          preferredChannel: c.preferredChannel || '',
          responsibility: c.responsibility || '',
        })}
        fromForm={(f) => ({
          name: (f.name || '').trim(),
          role: (f.role || '').trim(),
          organisation: (f.organisation || '').trim(),
          preferredChannel: f.preferredChannel || '',
          responsibility: (f.responsibility || '').trim(),
        })}
        keepIf={(p) => p.name}
        onReplace={saveContacts}
        renderItem={(c) => (
          <>
            <b>{c.name}</b>
            <small>
              {[c.role, c.organisation].filter(Boolean).join(' · ')}
            </small>
            {c.responsibility && <p>{c.responsibility}</p>}
            {c.preferredChannel && (
              <span className="stakeholder-channel">
                Preferred: {c.preferredChannel}
              </span>
            )}
          </>
        )}
      />

      <section className="card">
        <div className="card-head">
          <h2>Portfolio</h2>
        </div>
        {client.projects.length === 0 ? (
          <p className="muted-text">No projects for this client.</p>
        ) : (
          <div className="portfolio-list">
            {client.projects.map((project) => (
              <button
                type="button"
                key={project.id}
                className="portfolio-row"
                onClick={() => onOpenProject(project.id)}
              >
                <ProjectLabel project={project} />
                <span className="portfolio-row-meta">
                  <StatusBadge status={project.status} />
                  <span className="muted-text">
                    {project.spentMD.toFixed(1)} MD spent
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// A tiny inline list for the health reasons: one text field per row, saved
// as a plain string array through the relationship endpoint.
function ReasonsEditor({ notes, onSave }) {
  const items = (notes || []).map((text, i) => ({ id: String(i), text }));
  return (
    <EditableList
      bare
      title="Why this health"
      items={items}
      fields={[{ key: 'text', label: 'Reason', type: 'textarea', full: true }]}
      addLabel="Add reason"
      emptyLabel="Not set."
      toForm={(item) => ({ text: item.text || '' })}
      fromForm={(f) => ({ text: (f.text || '').trim() })}
      keepIf={(p) => p.text}
      onReplace={(rows) => onSave(rows.map((r) => r.text))}
      renderItem={(item) => <p>• {item.text}</p>}
    />
  );
}
